import moment, { Moment } from 'moment';
import { WP_REST_API_Post } from 'wp-types';
import {
  DEFAULT_AVATAR_URL,
  HouseArray,
  NewsView,
  User,
  WP_DirUser,
  WP_Event,
  WP_HouseRef,
  WP_HouseTerm,
  WP_News,
  WP_ServiceTerm,
  WP_UserNetwork,
} from '../model/models';
import { WP_Tag } from '../model/tags/tags';

export const DEFAULT_AUTHOR_IMAGE = '';
export const DEFAULT_AUTHOR_NAME = '...';
export const DEFAULT_SERVICES_PATH = '/services/tabs/campus';
export class ModelsService {
  public static getUserHouseRef = (
    user: User | WP_DirUser | undefined
  ): WP_HouseRef | undefined => {
    return Array.isArray(user?.acf?.user_house)
      ? user?.acf?.user_house[0]
      : undefined;
  };

  public static getHouseTerm = (
    houseId: number,
    allHouses: HouseArray
  ): WP_HouseTerm | undefined => {
    const filtered = allHouses.filter((h) => {
      return houseId === h.id;
    });
    return filtered.length > 0 ? filtered[0] : undefined;
  };

  public static getServiceTerm = (
    serviceId: number,
    allServices: WP_ServiceTerm[]
  ): WP_ServiceTerm | undefined => {
    const filtered = allServices.filter((s) => {
      return serviceId === s.id;
    });
    return filtered.length > 0 ? filtered[0] : undefined;
  };

  public static getHouseNameFromTermId(
    idHouseRef: number | undefined,
    allHouses: HouseArray,
    defaultName: string
  ): string {
    let houseName = defaultName;
    if (idHouseRef) {
      const house = ModelsService.getHouseTerm(idHouseRef, allHouses);
      houseName = ModelsService.getHouseNameFromTerm(house, houseName);
    }
    return houseName;
  }

  public static getHouseNameFromTerm(
    house: WP_HouseTerm | undefined,
    defaultName: string = DEFAULT_AUTHOR_NAME
  ) {
    return house?.acf?.common_name
      ? house?.acf?.common_name
      : house?.name
      ? house?.name
      : defaultName;
  }

  public static getServiceNameFromTerm(
    service: WP_ServiceTerm | undefined,
    locale: string | undefined,
    defaultName: string = DEFAULT_AUTHOR_NAME
  ) {
    let ret = defaultName;
    if (service) {
      ret = service.name;
      if (locale) {
        ret = ModelsService.getTranslationFromTerm(service, locale);
      }
    }
    return ret;
  }

  public static getTranslationFromTerm(
    term: WP_ServiceTerm | WP_Tag,
    locale?: string
  ) {
    let ret = term.name;
    if (term.acf.label && locale) {
      const localeLang = locale.length === 2 ? locale : locale.split('_')[0];
      const translation = term.acf.label.find((tr) => tr.lang === localeLang);
      if (translation) {
        ret = translation.value;
      }
    }

    return ret;
  }

  public static getHouseThumbnail(house: WP_HouseTerm | undefined) {
    return house?.acf?.image?.sizes?.thumbnail || DEFAULT_AUTHOR_IMAGE;
  }

  public static getServiceThumbnail(service: WP_ServiceTerm | undefined) {
    return service?.acf?.image?.sizes?.thumbnail || DEFAULT_AUTHOR_IMAGE;
  }

  public static toNewsView = (
    mainHouse: number,
    userHouseId: number,
    news: WP_News,
    allHouses: WP_HouseTerm[],
    allServices: WP_ServiceTerm[],
    locale?: string
  ): NewsView => {
    const authorType = ModelsService.getNewsAuthorType(news);

    const houseRef = ModelsService.getNewsHouse(mainHouse, news, userHouseId);
    const houseId = houseRef?.term_id || 0;
    const house = ModelsService.getHouseTerm(houseId, allHouses);

    const serviceRef =
      authorType === 'Service' && news.acf.service
        ? news.acf.service
        : undefined;
    const serviceId = serviceRef?.term_id || 0;
    const service = ModelsService.getServiceTerm(serviceId, allServices);

    const authorImgUrl =
      authorType === 'Service'
        ? ModelsService.getServiceThumbnail(service)
        : ModelsService.getHouseThumbnail(house);
    const authorName =
      authorType === 'Service'
        ? ModelsService.getServiceNameFromTerm(service, locale)
        : ModelsService.getHouseNameFromTerm(house);
    return {
      postId: news.id,
      houseId: houseId,
      title: news.title.rendered,
      authorImgUrl: authorImgUrl,
      authorName: authorName,
      news_date: news.date,
      thumbnail_img: news.fimg_url ? news.fimg_url : null,
      short_description: news.excerpt.rendered,
      description: news.content.rendered,
      slug: news.slug,
      alert: news.acf.alert,
      sticky: news.sticky,
      faq: news.acf.faq,
      form: news.acf.form,
      gform: news.acf.gform,
    };
  };

  /**
   * Compute the house of a news.
   * Take in priority the user's house, then take the main house.
   * @param houseId
   * @param mainHouseId
   * @param news
   * @returns
   */
  public static getNewsHouse = (
    mainHouseId: number,
    news: WP_News,
    userHouseId: number
  ): WP_HouseRef | undefined => {
    let newsHouse: WP_HouseRef | undefined = undefined;

    if (Array.isArray(news.acf.house)) {
      const ciupHouse = news.acf.house.find((h) => h.term_id === mainHouseId);
      const otherHouses = ciupHouse
        ? news.acf.house.filter((h) => h.term_id !== mainHouseId)
        : news.acf.house;
      const userHouse = otherHouses.find((h) => h.term_id === userHouseId);
      const firstHouse = otherHouses.slice(0, 1).shift();

      newsHouse = userHouse || firstHouse || ciupHouse;
    }

    return newsHouse;
  };

  /**
   * Get the type of the author of the news (House or Service)
   * @param news
   */
  public static getNewsAuthorType = (news: WP_News): 'House' | 'Service' => {
    const type = news.acf.service ? 'Service' : 'House';
    return type;
  };

  /**
   * Get full slug of the given page
   */
  public static getPageFullSlug = (page: WP_REST_API_Post): string => {
    const paths: string[] = new URL(page.link).pathname.split('/').slice(1);
    const link = paths ? '/' + paths.join('/') : '#';
    return link.substr(0, link.length - 1);
  };

  public static getServiceFullSlug = (service: WP_REST_API_Post): string => {
    const paths: string[] = new URL(service.link).pathname.split('/').slice(2);
    const link = paths ? '/services/' + paths.join('/') : '#';
    return link.substr(0, link.length - 1);
  };

  /**
   * Look for ancestors and builds an array.
   * The first item of the returned array is the given service.
   */
  public static getAncestors = (
    page: WP_REST_API_Post,
    pages: WP_REST_API_Post[]
  ): WP_REST_API_Post[] => {
    let current: WP_REST_API_Post | undefined = page;
    const ancestors: WP_REST_API_Post[] = [page];
    while (current && current.parent && current.parent > 0) {
      const parentId = current.parent;
      current = pages.find((it) => it.id === parentId);
      if (current) {
        ancestors.push(current);
      }
    }
    return ancestors;
  };

  public static eventComparator = (now: Moment, a: WP_Event, b: WP_Event) => {
    const aDist: number = ModelsService.eventDist(now, a);
    const bDist: number = ModelsService.eventDist(now, b);
    /* console.log(
      `DEBUG Event ${a.title.rendered} [${aDist}] VS Event ${b.title.rendered} [${bDist}]`
    ); */
    return aDist - bDist;
  };

  public static eventDist = (now: Moment, event: WP_Event) => {
    const start = moment(event.acf.start);
    const end = moment(event.acf.end);
    return Math.min(
      Math.abs(now.diff(start, 'hours')),
      Math.abs(now.diff(end, 'hours'))
    );
  };

  public static sortEvents = (events: WP_Event[]): WP_Event[] => {
    const now = moment();
    return events.sort((a, b) => {
      return ModelsService.eventComparator(now, a, b);
    });
  };

  /** don't display users without name and photo */
  public static userDirectoryFilter = (
    u: WP_DirUser,
    currentUserId: number
  ): boolean => u.name !== '' || u.acf.photo !== '';

  public static userTypeGia = (user: User) => {
    const userTypeGiaDefaultValue = {
      default: {
        color: 'text-residentColor',
        bg: 'bg-residentBg',
        svg: 'text-residentSvg',
      },
      'assimile-resident': {
        color: 'text-assimileResidentColor',
        bg: 'bg-assimileResidentBg',
        svg: 'text-assimileResidentSvg',
      },
      'assimile-salarie': {
        color: 'text-assimileEmployeeColor',
        bg: 'bg-assimileEmployeeBg',
        svg: 'text-assimileEmployeeSvg',
      },
      resident: {
        color: 'text-residentColor',
        bg: 'bg-residentBg',
        svg: 'text-residentSvg',
      },
      employee: {
        color: 'text-employeeColor',
        bg: 'bg-employeeBg',
        svg: 'text-employeeSvg',
      },
      other: {
        color: 'text-residentColor',
        bg: 'bg-residentBg',
        svg: 'text-residentColor',
      },
      api: {
        color: 'text-residentColor',
        bg: 'bg-residentBg',
        svg: 'text-residentColor',
      },
    };

    if (user !== undefined) {
      if (
        user?.acf?.user_gia_type === undefined ||
        user?.acf?.user_gia_type === ''
      ) {
        user.acf.user_gia_type = 'default';
      }
      return userTypeGiaDefaultValue[user?.acf?.user_gia_type];
    } else {
      return userTypeGiaDefaultValue['default'];
    }
  };

  public static getUserAvatar(user: WP_DirUser | undefined) {
    return user?.acf?.photo && user.acf.photo !== ''
      ? user.acf.photo
      : DEFAULT_AVATAR_URL;
  }

  public static getUserActiveHouse(user: User | WP_DirUser | undefined) {
    return user?.acf?.user_house &&
      Array.isArray(user.acf.user_house) &&
      user.acf.user_house.length > 0
      ? user.acf.user_house[0]
      : undefined;
  }

  public static getUserHouseName(user: User | WP_DirUser | undefined) {
    const house = ModelsService.getUserActiveHouse(user);
    return house ? house.name : '';
  }

  public static getMyFriends(
    usersNetwork: WP_UserNetwork[],
    currentUserId: number
  ): { network: WP_UserNetwork; friendId: number }[] {
    return (
      usersNetwork
        .filter(
          (un) =>
            (currentUserId === un.acf.user_a ||
              currentUserId === un.acf.user_b) &&
            (un.acf.relation_status === 'accepted' ||
              un.acf.relation_status === 'requested')
        )
        // compute friendId (can be user_a or user_b)
        .map((un, key) => ({
          friendId:
            un.acf.user_a !== currentUserId ? un.acf.user_a : un.acf.user_b,
          network: un,
        }))
        // remove duplicates on friendId
        .filter(
          (it, i, arr) => arr.findIndex((t) => t.friendId === it.friendId) === i
        )
    );
  }

  public static getParentTag = (parentId: number, parentTags: WP_Tag[]) => {
    const find = parentTags.find((p) => parentId === p.id);
    return find;
  };

  public static hash: (string: string, algo: 'SHA-256') => Promise<string> = (
    string,
    algo
  ) => {
    const utf8 = new TextEncoder().encode(string);
    return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((bytes) => bytes.toString(16).padStart(2, '0'))
        .join('');
      return hashHex;
    });
  };
}
